import { ajaxController, RequestOptions } from "./controller/AjaxController";
import { SuccessfulLoginResponse } from "../types/Auth/SuccessfulLoginResponse";
import { RefreshResponse } from "../types/Auth/RefreshResponse";
import { UserDto } from "../DTOs/User/UserDto";
import { SaveUserDto } from "../DTOs/User/SaveUserDto";
import { UpdateOwnProfileRequest } from "../DTOs/User/UpdateOwnProfileRequest";
import { PagedResult } from "../DTOs/Common/PagedResult";
import { UserRoleDto } from "../DTOs/User/UserRoleDto";
import { SsoProviderKey } from "../utilities/helpers/pageVocabulary.generated";
import { ActionResponse } from "../DTOs/Common/ErrorResponse";

const SIGNUP_CONVERSION_HEADER = "demo-signup-conversion";

type CreateUserRequest = {
  user: SaveUserDto, password: string
}

type UpdateUserPasswordRequest = {
  userId: string, newPassword: string
}

type UpdateSelfPasswordRequest = {
  oldPassword: string, newPassword: string
}

type LoginModel = {
  email: string, password: string
}

type RefreshRequest = {
  refreshToken: string;
};

type ExternalLoginRequest = {
  provider: SsoProviderKey, idToken: string, zoneIds: number[]
};

type LinkedInLoginRequest = {
  code: string, state: string, nonce: string | null, zoneIds: number[]
};

export type SignupZone = { id: number, name: string };

export type ExternalLoginResponse = ActionResponse<SuccessfulLoginResponse | null> & { signupConversion?: string };

export type SsoProviders = {
  google: { clientId: string } | null,
  microsoft: { clientId: string, authority: string } | null,
  linkedIn: boolean,
  signup: { providers: SsoProviderKey[], zones: SignupZone[], url: string } | null
};

export class UserAccountAjax {
  private _ajaxController = ajaxController;
  private _accountEndpoint = "/User"

  public getUsers() {
    return ajaxController.get<PagedResult<UserDto>>(
      `${this._accountEndpoint}/list`,
      {}
    );
  }

  public addUser(newUser: SaveUserDto, password: string) {
    return this._ajaxController.post<CreateUserRequest, UserDto>(
      `${this._accountEndpoint}`,
      { user: newUser, password: password}
    );
  }

  public updateUser(updatedUser: SaveUserDto) {
    return this._ajaxController.put<SaveUserDto, UserDto>(
      `${this._accountEndpoint}`,
      updatedUser
    );
  }

  public updateOwnProfile(request: UpdateOwnProfileRequest) {
    return this._ajaxController.put<UpdateOwnProfileRequest, UserDto>(
      `${this._accountEndpoint}/self`,
      request
    );
  }

  public setOwnLanguage(language: string, options?: RequestOptions) {
    return this._ajaxController.put<{ language: string }, null>(
      `${this._accountEndpoint}/self/language`,
      { language },
      options
    );
  }

  public updatePassword(userId: string, newPassword: string) {
    return this._ajaxController.put<UpdateUserPasswordRequest>(
      `${this._accountEndpoint}/password`,
      { userId, newPassword }
    );
  }

  public updateOwnPassword(oldPassword: string, newPassword: string) {
    return this._ajaxController.put<UpdateSelfPasswordRequest>(
      `${this._accountEndpoint}/self/password`,
      { oldPassword, newPassword }
    );
  }

  public setUserDisabled(userId: string, disabled: boolean) {
    return this._ajaxController.put<{ disabled: boolean }, UserDto>(
      `${this._accountEndpoint}/${userId}/disabled`,
      { disabled }
    );
  }

  public deleteUser(userId: string) {
    return this._ajaxController.delete(
      `${this._accountEndpoint}/${userId}`
    );
  }

  getRoles() {
    return ajaxController.get<PagedResult<UserRoleDto>>(
      `${this._accountEndpoint}/roles`,
      {}
    );
  }

  public getMyInfo() {
    return ajaxController.get<UserDto>(
      `${this._accountEndpoint}/me`,
      {}
    );
  }

  // auth
  login(username: string, password: string) {
    return ajaxController.post<LoginModel, SuccessfulLoginResponse>(
      `${this._accountEndpoint}/login`,
      {email: username, password}
    );
  }

  // Note: bounded, because every authenticated request waits on a refresh in progress, so one that hangs on
  // a dying link would stall the whole app rather than one call.
  refreshToken(refreshToken: string) {
    return this._ajaxController.post<RefreshRequest, RefreshResponse>(
      `${this._accountEndpoint}/refresh`,
      {refreshToken},
      { skipAuth: true, timeoutMs: 15_000 }
    );
  }

  getSsoProviders() {
    return this._ajaxController.get<SsoProviders>(`${this._accountEndpoint}/external-login/providers`, {});
  }

  loginWithProvider(provider: SsoProviderKey, idToken: string, zoneIds: number[]) {
    return this.loginExternally<ExternalLoginRequest>(`${this._accountEndpoint}/external-login`, {provider, idToken, zoneIds});
  }

  addDemoZones(zoneIds: number[]) {
    return this._ajaxController.post<{ zoneIds: number[] }, null>(`${this._accountEndpoint}/self/demo-zones`, {zoneIds});
  }

  loginWithLinkedIn(code: string, state: string, nonce: string | null, zoneIds: number[]) {
    return this.loginExternally<LinkedInLoginRequest>(`${this._accountEndpoint}/external-login/linkedin`, {code, state, nonce, zoneIds});
  }

  private async loginExternally<T>(endpoint: string, request: T): Promise<ExternalLoginResponse> {
    let signupConversion: string | undefined;
    const response = await this._ajaxController.post<T, SuccessfulLoginResponse>(endpoint, request, {
      skipAuth: true,
      onHeaders: headers => {
        const conversion = headers[SIGNUP_CONVERSION_HEADER];
        if (typeof conversion === "string") signupConversion = conversion;
      },
    });

    return signupConversion ? {...response, signupConversion} : response;
  }
}

export const userAccountAjax = new UserAccountAjax();